import { useState } from 'react';
import { getAnomalies } from '@entities';
import { useAsyncResource } from '@hooks';
import { FileEvidence } from '@features/evidence';
import DetailDialog from '@/shared/components/DetailDialog/DetailDialog';
import AnomalyDetail from '@/features/anomaly-register/components/AnomalyDetail';
import { analyzeReportReferences, reportDateLabel } from '@/entities/report/report.model';
import styles from './ReportDetail.module.css';

function ReferencingAnomalies({ companyId, report }) {
  const [expanded, setExpanded] = useState(null);
  const resource = useAsyncResource(async () => {
    // Match the same endpoint as the working Anomaly Register.
    // A fresh read prevents a previously cached empty result lasting 12 hours.
    const rows = await getAnomalies({ companyId, forceRefresh: true });
    if (!Array.isArray(rows)) throw new Error('The anomaly API must return a list.');

    return analyzeReportReferences(rows, report);
  }, [companyId, report.siteId, report.assetId, report.number, report.type], { initialData: null });

  function reload() {
    setExpanded(null);
    resource.reload();
  }

  if (resource.isLoading) return <p role="status">Loading report references…</p>;
  if (resource.error) return <div>
    <p role="alert">{resource.error}</p>
    <button type="button" className={styles.button} onClick={reload}>Retry references</button>
  </div>;
  const result = resource.data;
  if (!result) return null;
  const inconsistent = result.missingLocation + result.differentLocation;
  return <>
    <button type="button" className={styles.button} onClick={reload}>Refresh anomaly references</button>
    {!result.matches.length && <p role="status">No anomalies in the current company list reference this report number. This does not establish the inspection outcome.</p>}
    {inconsistent > 0 && <p className={styles.note}>
      {inconsistent} linked anomaly record(s) need a location check: {result.missingLocation} missing a site or asset,
      {' '}{result.differentLocation} recorded at a different location.
    </p>}
    <ul className={styles.findings}>
      {result.matches.map((row, index) => <li key={JSON.stringify([row.anomaly_ref, index])}>
        <button type="button" className={styles.finding} aria-expanded={expanded === index}
          onClick={() => setExpanded(expanded === index ? null : index)}>
          <strong>{row.anomaly_ref || 'Anomaly without reference'}</strong>
          <span>{row.rds_component || 'Component not recorded'} · {row.failure_mode || 'Failure mode not recorded'}</span>
          <span>Severity: {row.anomaly_severity ?? 'Not recorded'}</span>
          <span>{expanded === index ? 'Hide finding' : 'View finding'}</span>
        </button>
        {expanded === index && <AnomalyDetail content={row} />}
      </li>)}
    </ul>
  </>;
}

export default function ReportDetail({ report, companyId, onClose }) {
  const [widePreview, setWidePreview] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showReferences, setShowReferences] = useState(false);
  const canLoad = Boolean(report.siteId && report.assetId && report.fileName);
  const canReference = Boolean(companyId && report.number);
  const metadata = [
    ['Report ID', report.id], ['Report number', report.number],
    ['Report type', report.type], ['Report date', reportDateLabel(report.date)],
    ['Site', report.siteLabel], ['Asset', report.assetLabel],
    ['Inspection technique', report.techniques.join(' · ')],
    ['Filename (existing naming convention)', report.fileName],
  ];

  return <DetailDialog eyebrow="REPORT EVIDENCE" title={report.number || 'Report details'}
    subtitle={report.assetLabel + ' · ' + report.siteLabel} onClose={onClose}>
    <div className={styles.container}>
      <div className={styles.summary}>
        <div><span className={styles.eyebrow}>DOCUMENT CONTEXT</span>
          <h3>{report.number || 'Report without reference'}</h3>
          <p>{report.siteLabel} / {report.assetLabel}</p>
        </div>
        <span className={styles.badge}>{report.type}</span>
      </div>
      <div className={styles.layout} data-wide={widePreview}>
        <section className={styles.card}>
          <h3>Report record</h3>
          <dl className={styles.metadata}>
            {metadata.map(([label, value]) => <div key={label}>
              <dt>{label}</dt><dd>{value || 'Not recorded'}</dd>
            </div>)}
          </dl>
          <p className={styles.note}>Technique is derived only from a recognised report-type code. Report date is supplied by the report list; revision is not recorded.</p>
        </section>
        <section className={styles.card}>
          <h3>PDF evidence</h3>
          <div className={styles.toolbar}>
            <button type="button" className={styles.button} aria-pressed={widePreview}
              onClick={() => setWidePreview(value => !value)}>
              {widePreview ? 'Restore split view' : 'Widen PDF'}
            </button>
            {canLoad && <button type="button" className={styles.button}
              onClick={() => setRefreshKey(value => value + 1)}>Refresh PDF link</button>}
          </div>
          <div className={styles.preview}>
            {canLoad ? <FileEvidence companyId={companyId} siteId={report.siteId}
              assetId={report.assetId} folder="reports" fileName={report.fileName}
              kind="report" preview refreshKey={refreshKey} /> :
              <p className={styles.note}>The asset must resolve to one site, and a report number must be available before the PDF can be loaded.</p>}
          </div>
        </section>
      </div>
      <section className={styles.card}>
        <h3>Anomaly references</h3>
        <div className={styles.body}>
          <p className={styles.note}>Linked anomalies match this report number or its site-prefixed RVI reference within the company. Location inconsistencies are flagged separately. These links do not establish inspected-component coverage or a report revision.</p>
          {canReference ? <>
            <button type="button" className={styles.button} aria-expanded={showReferences}
              onClick={() => setShowReferences(value => !value)}>
              {showReferences ? 'Hide anomaly references' : 'Load anomaly references'}
            </button>
            {showReferences && <ReferencingAnomalies companyId={companyId} report={report} />}
          </> : <p>A report number and company are needed to look up anomaly references.</p>}
        </div>
      </section>
    </div>
  </DetailDialog>;
}